import { State } from '@progress/kendo-data-query';
import moment from 'moment-timezone';
import { useCallback, useEffect, useState } from 'react';
import uuid from 'react-uuid';
import { api } from 'src/constants/apiRoutes';
import useAsync from 'src/hooks/useAsync';
import {
  CheckbookLineItemError,
  CheckbookLineItemInfo,
  CheckbookLineItemWarning,
} from 'src/sections/view_checkbook/useCheckbookYearErrors';
import axiosInstance from 'src/utils/axios';
import { GLCodePeriodDepartment, GLCodeYearDepartment } from './useGlCodeDepartmentApi';
export interface GLCodesBudgetByPeriodBudgets {
  glPeriodId: number;
  glCdId?: number;
  startDate: string;
  endDate: string;
  totalBudgetAmount: number;
  initialBudget: number | null;
  isClosed?: number;
}

export interface GLCodesBudgetByYearResponseObject {
  id: number;
  code: string;
  companyName?: string;
  description: string;
  remainingBudgetAmount: number;
  budgetAmount: number;
  initialBudget: number | null;
  holdAmount: number;
  spentAmount: number;
  isActive: boolean;
  isDeleted?: boolean;
  hasHoldTransactionData?: boolean;
  hasSpentTransactionData?: boolean;
  isFixed: boolean;
  remainingBudgetAmountPercentage: number;
  selected: boolean;
  isNewGlCode?: boolean;
  rowId: string;
  departments: GLCodeYearDepartment[];
  department?: string;
  periodBudgets: GLCodesBudgetByPeriodBudgets[];
  isBudgetCopyAcrossEnabled?: boolean;
  warnings?: CheckbookLineItemWarning;
  errors?: CheckbookLineItemError;
  informations?: CheckbookLineItemInfo;
}
export interface IGLCodesBudgetByYearApiProps {
  fiscalYear?: number;
  checkbookDataState?: State;
  clientSide?: boolean;
}
const useGlCodesBudgetByYearApi = (companyId: string, department: GLCodePeriodDepartment) => {
  const [departmentFilteredGlCodeYearBudget, setDepartmentFilteredGlCodeYearBudget] = useState<
    GLCodesBudgetByYearResponseObject[]
  >([]);

  const call = useCallback(
    async (fiscalYear?: number) => {
      if (!companyId) {
        return [];
      }
      try {
        const res = await axiosInstance.get<GLCodesBudgetByYearResponseObject[]>(
          api.glCodesBudgetByYear(companyId, fiscalYear)
        );

        const data = res.data?.map((item) => {
          let periodBudgets = [...item.periodBudgets];

          periodBudgets.sort((a, b) => {
            const dateA: Date = new Date(a.startDate);
            const dateB: Date = new Date(b.startDate);
            return dateA.getTime() - dateB.getTime();
          });

          periodBudgets = periodBudgets.map((p) => ({
            ...p,
            startDate: moment(p.startDate).startOf('day').format(),
            endDate: moment(p.endDate).endOf('day').format(),
            isClosed: p.isClosed,
          }));

          const combinedPeriods = periodBudgets.reduce((result, item, index) => {
            const key = `period${(index + 1).toString().padStart(2, '0')}`;
            const key2 = `budgetPeriod${(index + 1).toString().padStart(2, '0')}`;
            const key3 = `initialBudgetPeriod${(index + 1).toString().padStart(2, '0')}`;
            const key4 = `_initialBudgetPeriod${(index + 1).toString().padStart(2, '0')}`;
            const key5 = `_isClosed${index + 1}`;

            result[key] = item.totalBudgetAmount;
            result[key2] = item.totalBudgetAmount;
            result[key3] = item.initialBudget;
            result[key4] = item.initialBudget;
            result[key5] = Number(item.isClosed);
            return result;
          }, {} as Record<string, number | null>);

          return {
            ...item,
            ...combinedPeriods,
            periodBudgets,
            rowId: uuid(),
          };
        });

        return data;
      } catch (error) {
        throw new Error(error);
      }
    },
    [companyId]
  );
  const { isLoading, value, execute } = useAsync(
    call,
    false,
    undefined,
    [] as GLCodesBudgetByYearResponseObject[]
  );

  useEffect(() => {
    const data =
      department?.departmentId === 0
        ? value
        : value.filter((item) =>
            item.departments.some(
              (dep: GLCodePeriodDepartment) => dep.departmentId === department.departmentId
            )
          );
    setDepartmentFilteredGlCodeYearBudget(data);
  }, [value, department]);

  return {
    isLoading,
    glCodesBudgetByYear: departmentFilteredGlCodeYearBudget,
    setGlCodesBudgetByYear: setDepartmentFilteredGlCodeYearBudget,
    getGlCodesBudgetByYear: execute,
  };
};
export default useGlCodesBudgetByYearApi;
